import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Settings,
  CheckCircle,
  AlertTriangle,
  Info,
  FileText,
  Pencil,
  PlusCircle,
  Download,
  ClipboardList,
  LoaderPinwheel,
  UtilityPole,
  Flame,
  CircleDashed,
} from "lucide-react";

import { MeterSearch } from "./MeterSearch";
import { useMxDashboard } from "../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { usePageViewTimer } from "@shared/hooks/usePageViewTimer";
import { QuickActions, QuickActionItem } from "@shared/components/QuickActions";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { useGetDashboardData } from "@shared/api/hooks";
import DecisionModal from "@features/mx/resources/components/create/DecisionModal";

export const MxDashboard: React.FC = () => {
  usePageViewTimer({ pageName: "MxDashboard" });
  const navigate = useNavigate();
  const remote_utility_id = useRemoteUtilityId();
  const [isDecisionModalOpen, setIsDecisionModalOpen] = useState(false);
const {data: mxDashboard} = useGetDashboardData('device_summary_dashboard');

  //hook for summary cards
  // const { data } = useMxDashboard(remote_utility_id);
  const data = mxDashboard?.metrics?.meterDeviceSummary;
  const meterConditions = data?.meterConditions;

  const staticCards = [
    {
      title: "Total Resources",
      count: data?.totalMeters || 0,
      trendSummary: "All registered meters",
      icon: <Settings className="h-8 w-8 text-blue-600" />,
    },
    {
      title: "Total Active Resources",
      count: data?.totalActiveMeters?.count || 0,
      trendSummary: `${
        data?.totalActiveMeters?.newThisMonth || 0
      } new this month`,
      icon: <CheckCircle className="h-8 w-8 text-emerald-600" />,
    },
  ];

  const getIcon = (type: string) => {
    switch (type) {
      case "water":
        return <LoaderPinwheel className="h-8 w-8 text-blue-600" />;
      case "electricity":
        return <UtilityPole className="h-8 w-8 text-amber-600" />;
      case "gas":
        return <Flame className="h-8 w-8 text-orange-600" />;
      default:
        return <CircleDashed className="h-8 w-8 text-gray-600" />;
    }
  };

  // looping over data to create card according to the meter types present in response.
  const dynamicMeterTypeCards = Object.entries(data?.meterTypes || {}).map(
    ([type, data]) => {
      const meter = data as { count: number; percentage: number };
      return {
        title: `${type.charAt(0).toUpperCase() + type.slice(1)} Resource`,
        count: meter.count || 0,
        trendSummary: `${meter.percentage}% of total`,
        icon: getIcon(type),
      };
    }
  );

  const conditionCards = [
    {
      title: "Normal Resources",
      count: meterConditions?.normal.count || 0,
      trendSummary: `${meterConditions?.normal.percentage || 0}% of total`,
      icon: <CheckCircle className="h-8 w-8 text-emerald-600" />,
    },
    {
      title: "Faulty Resources",
      count: meterConditions?.faulty.count || 0,
      trendSummary: `${meterConditions?.faulty.percentage || 0}% of total`,
      icon: <AlertTriangle className="h-8 w-8 text-red-600" />,
    },
    {
      title: "RCNT Resources",
      count: meterConditions?.rcnt.count || 0,
      trendSummary: `${meterConditions?.rcnt.percentage || 0}% of total`,
      icon: <Info className="h-8 w-8 text-purple-600" />,
    },
  ];

  const serviceCards = [
    {
      title: "Pending Service Orders",
      count: data?.pendingServiceOrders || 0,
      trendSummary: "Awaiting completion",
      icon: <FileText className="h-8 w-8 text-indigo-600" />,
    },
    {
      title: "Pending Installations",
      count: data?.pendingInstallations?.result?.total || 0,
      trendSummary: "Scheduled installations",
      icon: <Pencil className="h-8 w-8 text-cyan-600" />,
    },
  ];

  const cardData = [
    ...staticCards,
    ...dynamicMeterTypeCards,
    ...conditionCards,
    ...serviceCards,
  ];

  // Define quick actions
  const quickActions: QuickActionItem[] = [
    {
      title: "Add Single Resource",
      description: "Register a new resource device in the system",
      icon: PlusCircle,
      color:
        "bg-blue-50 hover:bg-blue-100 text-blue-800 border border-blue-200",
      onClick: () => setIsDecisionModalOpen(true),
    },
    {
      title: "Add Bulk Resources",
      description: "Import multiple resources via spreadsheet",
      icon: Download,
      color:
        "bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200",
      onClick: () => navigate("/onboarding/data-migration/data-cards?tab=upload"),
    },
    {
      title: "View All Resources",
      description: "Browse and search the complete resource inventory",
      icon: ClipboardList,
      color:
        "bg-green-50 hover:bg-green-100 text-green-800 border border-green-200",
      onClick: () => navigate("/mx/manage/resources"),
    },
    {
      title: "Raise Service Orders",
      description: "Create maintenance or inspection requests",
      icon: ClipboardList,
      color:
        "bg-purple-50 hover:bg-purple-100 text-purple-800 border border-purple-200",
      onClick: () => navigate("/mx/device-management/service-order"),
    },
  ];

  // current date and time formatting
  const currentDateTime = new Date()
    .toLocaleString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .replace("at", " |");

  return (
    <div>
      <div className="">
        <div>
          <h2 className="page-title">Resources Device Summary</h2>
          <p className="page-subtitle">{currentDateTime}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 w-full gap-4 my-4">
          {cardData.map((card, idx) => (
            <QuickStatsCard
              key={idx}
              title={card.title}
              count={card.count}
              trendSummary={card.trendSummary}
              icon={card.icon}
            />
          ))}
        </div>

        <MeterSearch />

        <div>
          <QuickActions
            actions={quickActions}
            heading={`Meter Services`}
            subheading={`Access meter service modules based on your role and permissions`}
          />
        </div>
      </div>
      <DecisionModal
        open={isDecisionModalOpen}
        onOpenChange={setIsDecisionModalOpen}
        onSelectWarehouse={() => {
          setIsDecisionModalOpen(false);
          navigate("/mx/manage/resources/create/warehouse");
        }}
        onSelectInstalled={() => {
          setIsDecisionModalOpen(false);
          navigate("/mx/manage/resources/create/installed");
        }}
      />
    </div>
  );
};

export default MxDashboard;
